import * as THREE from 'three'

import {createMuteState} from './audio/mute-state'
import {loadEngineScript} from './boot/engine-loader'
import {CANVAS_ID} from './config'
import {createAppModule} from './engine/app-module'
import {buildPipelineModules} from './engine/pipeline'
import {whenXr8Ready} from './engine/xr8-ready'
import {createHud} from './ui/hud'
import {askSoundConsent} from './ui/sound-consent'
import {createSoundToggle} from './ui/sound-toggle'
import {AppError, describeError} from './util/errors'

/**
 * Composition root. Wiring only - no logic lives here.
 */

// The engine reads three.js off the global object. A spread copy is used
// because an ES module namespace object is frozen and non-extensible, and the
// engine writes onto `window.THREE` on some code paths.
window.THREE = {...THREE}

const showFatal = (message: string): void => {
  // The consent dialog sits above the splash (z-index 40 vs 20) and may be up
  // when a boot step fails; without this line the fatal message would be
  // trapped underneath it, which is a black-screen-shaped dead end.
  document.getElementById('sound-consent')?.setAttribute('hidden', '')
  const splash = document.getElementById('splash')
  if (splash) {
    splash.hidden = false
    splash.textContent = message
  }
}

const boot = async (): Promise<void> => {
  const canvas = document.getElementById(CANVAS_ID)
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new AppError('CANVAS_MISSING', `#${CANVAS_ID}`)
  }

  const xrextras = window.XRExtras
  if (!xrextras) {
    throw new AppError('ENGINE_LOAD_FAILED', 'XRExtras global missing (script tag not loaded?)')
  }

  // Shown now, awaited just before xr8.run: the user reads and answers the
  // dialog while the engine download below proceeds underneath, instead of
  // staring at the splash first and a silent download after their tap.
  const consent = askSoundConsent()

  // P2 will run the pre-flight and in-app-browser gate here, before the ~6.5 MB
  // engine download starts. When it lands, the consent dialog above should move
  // after it - a user the gate turns away has no sound choice to make.
  await loadEngineScript()

  const xr8 = await whenXr8Ready()

  // Provisional gate, replaced by the full pre-flight in P2. Without it a
  // desktop browser gets a black screen and a console-only "No valid session
  // manager" error, because the engine refuses to start on an unsupported
  // device. Nothing may ever end in a black screen.
  if (!xr8.XrDevice.isDeviceBrowserCompatible()) {
    throw new AppError(
      'UNSUPPORTED_DEVICE',
      `incompatibleReasons=[${xr8.XrDevice.incompatibleReasons().join(', ')}]`
    )
  }

  const hud = createHud('hint')
  const muteState = createMuteState()

  xr8.addCameraPipelineModules(
    buildPipelineModules({xr8, xrextras, appModule: createAppModule({xr8, hud, muteState})})
  )

  // The dialog answer is the initial mute state; the toggle owns it from here.
  muteState.set(!(await consent))
  createSoundToggle(muteState)

  document.getElementById('splash')?.setAttribute('hidden', '')

  xr8.run({
    canvas,
    // Restricting to mobile stops the WebXR/headset session managers from
    // initialising, which is the only code path that would fetch
    // @webxr-input-profiles from jsDelivr. Keeps the app fully first-party.
    allowedDevices: xr8.XrConfig.device().MOBILE,
  })
}

boot().catch((error: unknown) => {
  console.error('[boot] failed', describeError(error))
  showFatal(error instanceof AppError ? error.userMessageJa : 'エラーが発生しました。読み込み直してね。')
})
